import { supabase } from '@/core/database/supabase';
import type { ProjectDashboardData, ProjectDashboardItem, ProjectFiltersState, ProjectSummary } from '../types/projects1.types';

const emptySummary: ProjectSummary = {
  total: 0,
  construction: 0,
  maintenance: 0,
  active: 0,
  completed: 0,
  planning: 0,
  paused: 0,
  totalContract: 0,
  totalContractConstruction: 0,
  totalContractMaintenance: 0,
};

function safeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function normalizeStatus(status?: string | null): string {
  if (!status) return 'planning';
  if (status === 'construction') return 'active';
  return status;
}

function normalizeType(type?: string | null): string {
  if (!type) return 'construction';
  if (type === 'landscape' || type === 'infrastructure') return 'construction';
  return type;
}

import { storageService } from '@/services/storage.service';

function toProject(row: Record<string, unknown>): ProjectDashboardItem {
  const progress = safeNumber(row.latest_progress_pct) || safeNumber(row.completion_pct);
  
  // Extract original URL
  let rawThumbnailUrl = (typeof row.thumbnail_url === 'string' ? row.thumbnail_url : null) || 
                   (typeof row.description === 'string' ? row.description.match(/\[METADATA\]: (\{.*\})/)?.[1]?.match(/"thumbnail":"(https?:\/\/\S+?)"/)?.[1] : null) || 
                   (typeof row.description === 'string' ? row.description.match(/\[THUMBNAIL\]: (https?:\/\/\S+)/)?.[1] : null) || null;

  // Optimize thumbnail URL for dashboard (200px width) if it's from our storage
  let optimizedThumbnailUrl = rawThumbnailUrl;
  if (rawThumbnailUrl && rawThumbnailUrl.includes('/storage/v1/object/public/media-assets/')) {
    const path = rawThumbnailUrl.split('/media-assets/').pop();
    if (path) {
      optimizedThumbnailUrl = storageService.getOptimizedUrl('media-assets', path, { width: 200, height: 150, quality: 80 });
    }
  }

  return {
    id: String(row.id ?? ''),
    code: String(row.code ?? 'GSP-0000'),
    name: String(row.name ?? 'Dự án chưa đặt tên'),
    client_name: typeof row.client_name === 'string' ? row.client_name : null,
    location: typeof row.location === 'string' ? row.location : null,
    project_type: normalizeType(row.project_type as string | null),
    status_code: normalizeStatus(row.status_code as string | null),
    thumbnail_url: optimizedThumbnailUrl,
    start_date: typeof row.start_date === 'string' ? row.start_date : null,
    end_date: typeof row.end_date === 'string' ? row.end_date : null,
    contract_amount: safeNumber(row.contract_amount),
    budget_amount: safeNumber(row.budget_amount),
    completion_pct: progress,
    latest_progress_pct: progress,
    pm: (row.pm as ProjectDashboardItem['pm']) ?? null,
    site_manager: (row.site_manager as ProjectDashboardItem['site_manager']) ?? null,
    created_at: typeof row.created_at === 'string' ? row.created_at : null,
  };
}

function applyClientFilters(items: ProjectDashboardItem[], filters: ProjectFiltersState): ProjectDashboardItem[] {
  const search = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    const matchesSearch = !search || [item.name, item.code, item.client_name, item.location]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(search));
    const matchesType = filters.type === 'all' || normalizeType(item.project_type) === filters.type;
    const matchesStatus = filters.status === 'all' || normalizeStatus(item.status_code) === filters.status;
    const matchesRegion = filters.region === 'all' || String(item.location ?? '').toLowerCase().includes(filters.region);
    return matchesSearch && matchesType && matchesStatus && matchesRegion;
  });
}

function buildSummary(items: ProjectDashboardItem[]): ProjectSummary {
  return items.reduce<ProjectSummary>((summary, item) => {
    const type = normalizeType(item.project_type);
    const status = normalizeStatus(item.status_code);
    const amount = safeNumber(item.contract_amount);
    
    summary.total += 1;
    summary.totalContract += amount;
    
    if (type === 'maintenance') {
      summary.maintenance += 1;
      summary.totalContractMaintenance += amount;
    } else {
      summary.construction += 1;
      summary.totalContractConstruction += amount;
    }
    
    if (status === 'completed') summary.completed += 1;
    else if (status === 'on_hold') summary.paused += 1;
    else if (status === 'planning') summary.planning += 1;
    else summary.active += 1;
    
    return summary;
  }, { ...emptySummary });
}

export const projectsListService = {
  async getDashboard(companyId: string, filters: ProjectFiltersState): Promise<ProjectDashboardData> {
    if (!companyId) {
      return { projects: [], total: 0, summary: emptySummary };
    }

    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        pm:user_profiles!pm_user_id(id, full_name, avatar_url:avatar_path),
        site_manager:user_profiles!site_manager_user_id(id, full_name, avatar_url:avatar_path)
      `)
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const allProjects = (data ?? []).map((row) => toProject(row as Record<string, unknown>));
    const filtered = applyClientFilters(allProjects, filters);
    const from = (filters.page - 1) * filters.pageSize;
    const pageProjects = filtered.slice(from, from + filters.pageSize);

    // Get top 3 upcoming projects (by end_date)
    const upcoming = allProjects
      .filter(p => p.end_date && p.status_code !== 'completed')
      .sort((a, b) => new Date(a.end_date!).getTime() - new Date(b.end_date!).getTime())
      .slice(0, 3);

    return {
      projects: pageProjects,
      upcoming,
      total: filtered.length,
      summary: buildSummary(allProjects),
    };
  },

  async deleteProject(id: string): Promise<void> {
    // 1. Get project info before deleting for audit log
    const { data: project } = await supabase
      .from('projects')
      .select('name, code, project_id')
      .eq('id', id)
      .single();

    // 2. Soft delete
    const { error } = await supabase
      .from('projects')
      .update({ 
        deleted_at: new Date().toISOString(),
        is_active: false 
      })
      .eq('id', id);

    if (error) throw error;

    // 3. Record Audit Log
    try {
      const { auditService } = await import('@/services/audit.service').then(m => ({ auditService: m.default }));
      await auditService.log({
        project_id: id, // For project itself, project_id is usually its own id
        action_code: 'delete',
        module_code: 'projects',
        entity_type: 'projects',
        entity_id: id,
        summary: `Xóa dự án: ${project?.code || '---'} - ${project?.name || '---'}`,
        before_data: project as any
      });
    } catch (logErr) {
      console.error('[projectsListService] Failed to record audit log:', logErr);
    }
  }
};
